/**
 * Client-side PDF text extraction for catalog import.
 * Uses pdfjs-dist to extract text per page, then heuristics to split into rows/columns.
 * Supports two formats: (1) table-style with one line per row and delimiters, (2) multi-line
 * product blocks (each product = 3–4 lines: id/sku/title/desc/price, then currency/category/image/inventory, then tags).
 */

export type PDFParseResult =
  | { ok: true; rawHeaders: string[]; rawRows: string[][] }
  | { ok: false; error: string };

const MAX_PAGES = 15;
const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10 MB

/** Line starts with digits and "SKU-" (product block format) */
const PRODUCT_BLOCK_START = /^\d+\s+SKU-/i;

/** Pagination / page markers to skip when grouping blocks */
const SKIP_LINE = /^(Page\s+\d+|--\s*\d+\s+of\s+\d+\s*--)$/i;

/** Split a line into cells by tab, comma, or 2+ spaces */
function splitLineToCells(line: string): string[] {
  const trimmed = line.trim();
  if (!trimmed) return [];
  if (trimmed.includes("\t")) {
    return trimmed.split(/\t/).map((c) => c.trim());
  }
  if (trimmed.includes(",")) {
    return trimmed.split(",").map((c) => c.trim());
  }
  return trimmed.split(/\s{2,}/).map((c) => c.trim());
}

/** Known headers for block format (order matches parseProductBlock output) */
const BLOCK_HEADERS = ["id", "sku", "title", "description", "price", "currency", "category", "imageUrl", "inventory", "tags"];

/**
 * Parse a single product block (3–4 lines) into a row of 10 columns.
 * Line 1: id SKU-XXX-NNNN title [Model NNN] description. price
 * Line 2: currency category imageUrl inventory
 * Line 3: tags (comma-separated)
 */
function parseProductBlock(lines: string[]): string[] {
  const line1 = lines[0] ?? "";
  const line2 = lines[1] ?? "";
  const line3 = lines[2] ?? "";

  let parsedId = "";
  let parsedSku = "";
  let parsedTitle = "";
  let parsedDesc = "";
  let parsedPrice = "";
  const idM = line1.match(/^(\d+)\s+/);
  if (idM) parsedId = idM[1];
  const skuM = line1.match(/\b(SKU-[A-Z]+-\d+)\b/i);
  if (skuM) parsedSku = skuM[1];
  const priceM = line1.match(/(\d+\.?\d*)\s*$/);
  if (priceM) parsedPrice = priceM[1];

  let middle = line1;
  if (parsedId) middle = middle.substring(parsedId.length).trim();
  if (parsedSku) middle = middle.replace(parsedSku, "").trim();
  if (parsedPrice) middle = middle.replace(new RegExp(`\\s*${parsedPrice.replace(".", "\\.")}\\s*$`), "").trim();

  const modelMatch = middle.match(/\s+Model\s+\d+\s+(.*)/);
  if (modelMatch) {
    parsedTitle = middle.substring(0, middle.indexOf(" Model ")).trim();
    parsedDesc = modelMatch[1].trim().replace(/\s*\.?\s*$/, "");
  } else {
    parsedTitle = middle;
  }

  let currency = "";
  let category = "";
  let imageUrl = "";
  let inventory = "";
  const urlMatch = line2.match(/(https?:\/\/\S+)/);
  if (urlMatch) imageUrl = urlMatch[1];
  const line2WithoutUrl = line2.replace(imageUrl, "").trim();
  const words = line2WithoutUrl.split(/\s+/).filter(Boolean);
  const lastWord = words[words.length - 1];
  if (words.length >= 1) currency = words[0];
  if (/^\d+$/.test(lastWord)) {
    inventory = lastWord;
    category = words.slice(1, -1).join(" ").trim();
  } else {
    category = words.slice(1).join(" ").trim();
  }

  const tags = line3.trim();

  return [
    parsedId,
    parsedSku,
    parsedTitle,
    parsedDesc,
    parsedPrice,
    currency,
    category,
    imageUrl,
    inventory,
    tags,
  ];
}

/**
 * Extract text from PDF and return a 2D grid (first row = rawHeaders, rest = rawRows).
 * Limits to first MAX_PAGES and MAX_FILE_BYTES.
 */
export async function parsePDFToRows(buffer: ArrayBuffer): Promise<PDFParseResult> {
  if (buffer.byteLength > MAX_FILE_BYTES) {
    return { ok: false, error: `PDF is too large (max ${MAX_FILE_BYTES / 1024 / 1024} MB).` };
  }

  try {
    const pdfjs = await import("pdfjs-dist");
    if (typeof pdfjs.GlobalWorkerOptions !== "undefined") {
      pdfjs.GlobalWorkerOptions.workerSrc = "https://unpkg.com/pdfjs-dist@5.3.93/build/pdf.worker.min.mjs";
    }
    const loadingTask = pdfjs.getDocument({ data: buffer });
    const pdf = await loadingTask.promise;
    const numPages = pdf.numPages;
    const pagesToRead = Math.min(numPages, MAX_PAGES);

    const allLines: string[] = [];
    for (let i = 1; i <= pagesToRead; i++) {
      const page = await pdf.getPage(i);
      const textContent = await page.getTextContent();
      const strings = (textContent.items as Array<{ str?: string }>)
        .filter((item): item is { str: string } => typeof (item as { str?: string }).str === "string")
        .map((item) => item.str);
      const pageText = strings.join(" ");
      const lines = pageText
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter(Boolean);
      allLines.push(...lines);
    }

    if (allLines.length === 0) {
      return { ok: false, error: "No text could be extracted from the PDF." };
    }

    const nonSkipLines = allLines.filter((l) => !SKIP_LINE.test(l.trim()));
    const blockStarts = nonSkipLines.filter((l) => PRODUCT_BLOCK_START.test(l));
    const useBlockFormat = blockStarts.length >= 3;

    if (useBlockFormat) {
      const rawRows: string[][] = [];
      for (let i = 0; i < nonSkipLines.length; i++) {
        if (PRODUCT_BLOCK_START.test(nonSkipLines[i])) {
          const nextTwo: string[] = [];
          for (let j = i + 1; j < nonSkipLines.length && nextTwo.length < 2; j++) {
            if (!SKIP_LINE.test(nonSkipLines[j])) nextTwo.push(nonSkipLines[j]);
          }
          rawRows.push(parseProductBlock([nonSkipLines[i], nextTwo[0] ?? "", nextTwo[1] ?? ""]));
        }
      }
      if (rawRows.length > 0) {
        return { ok: true, rawHeaders: [...BLOCK_HEADERS], rawRows };
      }
    }

    const rows: string[][] = allLines.map((line) => splitLineToCells(line));
    const maxCols = Math.max(...rows.map((r) => r.length), 1);
    const normalizedRows = rows.map((row) => {
      const padded = [...row];
      while (padded.length < maxCols) padded.push("");
      return padded;
    });

    const rawHeaders = normalizedRows[0].map((h) => String(h ?? "").trim());
    const rawRows = normalizedRows.slice(1).map((row) => row.map((c) => String(c ?? "").trim()));

    return { ok: true, rawHeaders, rawRows };
  } catch (e) {
    const message = e instanceof Error ? e.message : String(e);
    return { ok: false, error: `PDF parsing failed: ${message}` };
  }
}
